import { GAME_MODE } from '~/interfaces';
import { getAllStats, SongStats } from '~/modules/songs/stats/common';
import UserProfiles, { normaliseProfileName, ProfileWriteError, UserProfile } from '~/modules/user-profiles/profiles';
import storage from '~/modules/utils/storage';

const BACKFILLED_KEY = 'user-profiles-backfilled-from-plays';

// The name a slot with nobody on it scores under (`PlayerEntity.getName`) - a seat, not a singer.
const DEFAULT_SLOT_NAME = /^player \d+$/i;

/**
 * The singers who have plays on record but no profile - everyone a phone brought in before phones
 * made profiles of their own singers. Co-op rounds are left out: their one entry is the whole team,
 * filed under every name joined together.
 */
export const namesToBackfill = (stats: Record<string, SongStats | null | undefined>, profiles: UserProfile[]) => {
  const known = new Set(profiles.map((profile) => normaliseProfileName(profile.name)));
  const names = new Map<string, string>();

  Object.values(stats).forEach((songStats) =>
    songStats?.scores?.forEach((round) => {
      if (round.setup?.mode === GAME_MODE.CO_OP) return;

      round.scores.forEach(({ name }) => {
        const trimmed = name?.trim();
        const key = trimmed ? normaliseProfileName(trimmed) : '';
        if (!trimmed || DEFAULT_SLOT_NAME.test(trimmed) || known.has(key) || names.has(key)) return;

        names.set(key, trimmed);
      });
    }),
  );

  return [...names.values()];
};

/**
 * Runs once per browser. Remembered afterwards, so a profile somebody deletes on purpose is not
 * made again from its plays on the next launch.
 */
export const backfillProfilesFromPlays = async () => {
  if (storage.local.getItem<boolean>(BACKFILLED_KEY)) return;

  const names = namesToBackfill(await getAllStats(), UserProfiles.getAll());

  try {
    names.forEach((name) => UserProfiles.ensure(name));
  } catch (error) {
    // Left unmarked, so the ones that did not fit are tried again once there is room.
    if (error instanceof ProfileWriteError) return;
    throw error;
  }

  storage.local.setItem(BACKFILLED_KEY, true);
};
